import {
  useEffect, useCallback, useState, useRef, cloneElement, Children,
} from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown } from '@fortawesome/free-solid-svg-icons';
import PropTypes from 'prop-types';

const Menu = ({ children, title, label }) => {
  const [visible, setVisible] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const ref = useRef(null);
  const buttonRef = useRef(null);

  const childArray = Children.toArray(children);
  const itemCount = childArray.length;

  const handleClose = useCallback(() => {
    setVisible(false);
    setFocusedIndex(-1);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (visible && ref.current && !ref.current.contains(event.target)) {
        handleClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [visible, handleClose]);

  const handleButtonClick = (event) => {
    if (visible) {
      handleClose();
    } else {
      setVisible(true);
      // event.detail === 0 means keyboard activation (Enter/Space) — focus first item.
      if (event.detail === 0) { setFocusedIndex(0); }
    }
  };

  const handleButtonKeyDown = (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setVisible(true);
      setFocusedIndex(0);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setVisible(true);
      setFocusedIndex(itemCount - 1);
    }
  };

  const handleMenuKeyDown = (event) => {
    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        handleClose();
        buttonRef.current?.focus();
        break;
      case 'ArrowDown':
        event.preventDefault();
        setFocusedIndex(i => (i + 1) % itemCount);
        break;
      case 'ArrowUp':
        event.preventDefault();
        setFocusedIndex(i => (i <= 0 ? itemCount - 1 : i - 1));
        break;
      case 'Home':
        event.preventDefault();
        setFocusedIndex(0);
        break;
      case 'End':
        event.preventDefault();
        setFocusedIndex(itemCount - 1);
        break;
      case 'Tab':
        handleClose();
        break;
      default:
        break;
    }
  };

  return (
    <div className="menu" ref={ref}>
      <button
        type="button"
        ref={buttonRef}
        className="btn"
        aria-haspopup="menu"
        aria-expanded={visible}
        aria-label={label || undefined}
        onClick={handleButtonClick}
        onKeyDown={handleButtonKeyDown}
      >
        {title && <span className="title">{title}</span>}
        <FontAwesomeIcon icon={faCaretDown} aria-hidden="true" />
      </button>
      {visible && (
        <div className="items" role="menu" tabIndex={-1} onKeyDown={handleMenuKeyDown}>
          {childArray.map((child, index) => cloneElement(child, {
            onClose: handleClose,
            focused: focusedIndex === index,
          }))}
        </div>
      )}
    </div>
  );
};

Menu.propTypes = {
  children: PropTypes.node.isRequired,
  title: PropTypes.node,
  label: PropTypes.string,
};

Menu.defaultProps = {
  title: null,
  label: null,
};

const MenuItem = ({
  children, href, onClose, onClick, focused,
}) => {
  const linkRef = useRef(null);

  useEffect(() => {
    if (focused) { linkRef.current?.focus(); }
  }, [focused]);

  const handleClick = (event) => {
    if (onClose) { onClose(); }
    if (onClick) { onClick(event); }
  };

  return (
    <div className="item" role="none">
      <a
        ref={linkRef}
        onClick={handleClick}
        href={href}
        className="link"
        role="menuitem"
        tabIndex={focused ? 0 : -1}
      >
        {children}
      </a>
    </div>
  );
};

MenuItem.propTypes = {
  href: PropTypes.string,
  children: PropTypes.node.isRequired,
  // eslint-disable-next-line react/require-default-props
  onClose: PropTypes.func,
  // eslint-disable-next-line react/require-default-props
  onClick: PropTypes.func,
  // eslint-disable-next-line react/require-default-props
  focused: PropTypes.bool,
};

MenuItem.defaultProps = {
  href: null,
};

Menu.Item = MenuItem;

export default Menu;
